import { MetricCardSkeleton } from "@components/display/MetricCardSkeleton";
import { cn } from "@lib/utils/cn";
import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Inbox } from "lucide-react";
import type { ComponentType, KeyboardEvent, ReactNode } from "react";

/**
 * Semantic accent for a metric card. Resolved to token pairs by
 * {@link resolveAccentStyle} — never used as a raw Tailwind class.
 */
export type MetricAccent = "primary" | "positive" | "attention" | "negative" | "info" | "neutral";

export type MetricTrendDirection = "up" | "down" | "flat";

/** Drives the COLOR only — decoupled from direction (retards ↑ = negative tone). */
export type MetricTrendTone = "positive" | "negative" | "neutral";

/** Direction + tone are decoupled on purpose (cost ↑ = negative tone). */
export interface MetricTrend {
  /** Signed magnitude, ex: 12.5 → "+12,5 %" */
  value: number;
  direction: MetricTrendDirection;
  tone: MetricTrendTone;
  /** Optional visible text override (defaults to the formatted percentage) */
  label?: string;
}

/**
 * Card content state. The parent decides the state — the card NEVER infers
 * "empty" from the value (0 is valid data).
 */
export type MetricCardState =
  | { type: "loading" }
  | { type: "error"; message?: string; onRetry?: () => void }
  | { type: "empty"; message?: string; icon?: LucideIcon; action?: MetricAction }
  | {
      type: "ready";
      value: number | string;
      trend?: MetricTrend;
      comparison?: string;
      progress?: { value: number; max: number };
    };

/** Footer action — rendered as a native <button>, stopPropagation applied. */
export interface MetricAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: "link" | "button";
}

export interface MetricCardProps {
  label: string;
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  /** Semantic accent (icon container + progress fill). Default "neutral". */
  accent?: MetricAccent;
  prefix?: string;
  suffix?: string;
  /** Fraction digits for numeric values via Intl.NumberFormat("fr-FR"). Default 0. */
  decimals?: number;
  description?: string;
  iconShape?: "rounded" | "circle" | "square";
  size?: "sm" | "md" | "lg";
  variant?: "default" | "bordered" | "flat";
  state: MetricCardState;
  actions?: MetricAction[];
  /** Navigation target — preferred over onClick when the card navigates. */
  href?: string;
  onClick?: () => void;
  /** Chart slot (MiniSparkline, MiniBarChart, MiniDonut…) — card knows nothing of charts. */
  visualization?: ReactNode;
  /**
   * "block" (default): visualization renders below description (full width).
   * "inline": visualization sits beside the value row — compact layout for
   * metric cards with donuts/bars that shouldn't eat vertical space.
   */
  visualizationLayout?: "block" | "inline";
  /**
   * Hides the ready-state value row — donut-style visualizations carry the
   * number themselves (mockup: the donut variant REPLACES the value row).
   */
  hideValue?: boolean;
  className?: string;
  /** Renders a "Données anciennes" badge (fixture #13 amendment). */
  stale?: boolean;
}

type AccentStyle = { color: string; backgroundColor: string };

/** Contract §4 mapping — accent name → token pair (fg/bg). */
const ACCENT_STYLES: Readonly<Record<MetricAccent, AccentStyle>> = {
  primary: {
    color: "var(--color-primary)",
    backgroundColor: "color-mix(in oklab, var(--color-primary) 10%, transparent)",
  },
  positive: {
    color: "var(--status-completed)",
    backgroundColor: "var(--status-completed-bg)",
  },
  attention: {
    color: "var(--status-pending)",
    backgroundColor: "var(--status-pending-bg)",
  },
  negative: {
    color: "var(--status-rejected)",
    backgroundColor: "var(--status-rejected-bg)",
  },
  info: {
    color: "var(--status-in-progress)",
    backgroundColor: "var(--status-in-progress-bg)",
  },
  neutral: {
    color: "var(--color-base-content)",
    backgroundColor: "transparent",
  },
};

const SIZE_CLASSES = {
  sm: "p-3 gap-2 text-xs",
  md: "p-4 gap-2.5 text-sm",
  lg: "p-6 gap-3 text-base",
} as const;

const VALUE_SIZE_CLASSES = {
  sm: "text-lg",
  md: "text-2xl",
  lg: "text-3xl",
} as const;

const ICON_SIZE_CLASSES = {
  sm: "h-8 w-8 p-1.5 [&_svg]:h-4 [&_svg]:w-4",
  md: "h-10 w-10 p-2 [&_svg]:h-5 [&_svg]:w-5",
  lg: "h-12 w-12 p-2.5 [&_svg]:h-6 [&_svg]:w-6",
} as const;

const ICON_SHAPE_CLASSES = {
  rounded: "rounded-lg",
  circle: "rounded-full",
  square: "rounded-none",
} as const;

const VARIANT_CLASSES = {
  default: "bg-[var(--surface)] shadow-md",
  bordered: "border border-base-300 bg-[var(--surface)]",
  flat: "bg-base-200/60 hover:bg-base-200/80",
} as const;

/** Mockup pill arrows: 12×12 stroke paths. Flat renders NO icon (mockup parity). */
const TREND_ARROW_PATHS: Readonly<Record<Exclude<MetricTrendDirection, "flat">, string>> = {
  up: "M6 2.5v7M3 5.5l3-3 3 3",
  down: "M6 9.5v-7M3 6.5l3 3 3-3",
};

/** Tone → pill token pair (fg/bg). Neutral = base-content 10% mix + soft ink. */
const TREND_TONE_STYLES: Readonly<
  Record<MetricTrendTone, { color: string; backgroundColor: string }>
> = {
  positive: {
    color: "var(--status-completed)",
    backgroundColor: "var(--status-completed-bg)",
  },
  negative: {
    color: "var(--status-rejected)",
    backgroundColor: "var(--status-rejected-bg)",
  },
  neutral: {
    color: "var(--ink-soft)",
    backgroundColor: "color-mix(in oklab, var(--color-base-content) 10%, transparent)",
  },
};

const TREND_TONE_LABELS: Readonly<Record<MetricTrendTone, string>> = {
  positive: "positive",
  negative: "négative",
  neutral: "neutre",
};

const FORMATTER_CACHE = new Map<number, Intl.NumberFormat>();

/** fr-FR formatter cache — one instance per decimals count. */
function getFormatter(decimals: number): Intl.NumberFormat {
  const cached = FORMATTER_CACHE.get(decimals);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  FORMATTER_CACHE.set(decimals, formatter);
  return formatter;
}

/** Formats a numeric value ("127452", 0) → "127 452" / "0"; strings pass through. */
function formatMetricValue(value: number | string, decimals: number): string {
  if (typeof value === "string") return value;
  return getFormatter(decimals).format(value);
}

function resolveAccentStyle(accent: MetricAccent): AccentStyle {
  return ACCENT_STYLES[accent];
}

/** aria-label "{+/-}X %, tendance {tone}" — ex: "+12,5 %, tendance positive". */
function buildTrendAriaLabel(
  direction: MetricTrendDirection,
  tone: MetricTrendTone,
  value: number,
): string {
  const sign = direction === "up" ? "+" : direction === "down" ? "-" : "";
  return `${sign}${getFormatter(1).format(value)} %, tendance ${TREND_TONE_LABELS[tone]}`;
}

interface TrendIndicatorProps {
  direction: MetricTrendDirection;
  tone: MetricTrendTone;
  /** Signed magnitude for display + aria, ex: 12.5 */
  value: number;
  /** Optional visible text override (defaults to the formatted percentage) */
  label?: string;
}

/** Trend pill (mockup parity): arrow by DIRECTION, colors by TONE — flat or neutral = no icon. */
function TrendIndicator({ direction, tone, value, label }: TrendIndicatorProps) {
  const toneStyle = TREND_TONE_STYLES[tone];
  const sign = direction === "up" ? "+" : direction === "down" ? "-" : "";
  // Mockup: the neutral badge carries no icon ("stable") — a neutral tone means
  // no meaningful move, so the arrow is suppressed even if a direction slips in.
  const arrowPath =
    direction === "flat" || tone === "neutral" ? null : TREND_ARROW_PATHS[direction];
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[0.7rem] font-semibold"
      style={{ color: toneStyle.color, backgroundColor: toneStyle.backgroundColor }}
      role="img"
      aria-label={buildTrendAriaLabel(direction, tone, value)}
    >
      {arrowPath && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d={arrowPath}
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label ?? `${sign}${getFormatter(1).format(value)} %`}
    </span>
  );
}

/** Accent-colored progress bar over a base-300 track. */
function CardProgress({
  progress,
  accent,
}: {
  progress: { value: number; max: number };
  accent: MetricAccent;
}) {
  const ratio = progress.max > 0 ? Math.min(Math.max(progress.value / progress.max, 0), 1) : 0;
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-base-300"
      role="progressbar"
      aria-valuenow={progress.value}
      aria-valuemin={0}
      aria-valuemax={progress.max}
    >
      <div
        className="h-full rounded-full transition-[width] duration-[var(--duration-base)] ease-[var(--ease-out)]"
        style={{
          width: `${ratio * 100}%`,
          backgroundColor: resolveAccentStyle(accent).color,
        }}
      />
    </div>
  );
}

/** Footer actions — native buttons, stopPropagation so card onClick doesn't fire. */
function CardActions({ actions }: { actions: MetricAction[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          className={action.variant === "button" ? "btn btn-ghost btn-xs" : "btn btn-link btn-xs"}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
          }}
        >
          {action.icon && <action.icon className="h-3.5 w-3.5" aria-hidden="true" />}
          {action.label}
        </button>
      ))}
    </div>
  );
}

/** Error body: rejected-bg tint handled by the shell; message + optional retry. */
function ErrorStateContent({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex min-w-0 flex-col items-start gap-2">
      <span
        className="inline-flex items-center gap-1.5 font-medium"
        style={{ color: "var(--status-rejected)" }}
      >
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {message ?? "Erreur lors du chargement"}
      </span>
      {onRetry && (
        <button type="button" className="btn btn-ghost btn-xs" onClick={onRetry}>
          Réessayer
        </button>
      )}
    </div>
  );
}

/** Empty body: muted icon + message + optional single action. */
function EmptyStateContent({
  message,
  icon,
  action,
}: {
  message?: string;
  icon?: LucideIcon;
  action?: MetricAction;
}) {
  const Icon = icon ?? Inbox;
  return (
    <div className="flex min-w-0 flex-col items-start gap-2">
      <span className="inline-flex items-center gap-1.5 text-base-content/70">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {message ?? "Aucune donnée"}
      </span>
      {action && <CardActions actions={[action]} />}
    </div>
  );
}

/** Ready body: header (label + icon), value row, comparison, description, extras. */
function CardHeader({ props }: { props: MetricCardProps }) {
  const accentStyle = resolveAccentStyle(props.accent ?? "neutral");
  const Icon = props.icon;
  return (
    <div className="flex min-w-0 items-start justify-between gap-2">
      <span className="min-w-0 truncate font-medium text-base-content/70">{props.label}</span>
      {Icon && (
        <span
          className={cn(
            "inline-flex shrink-0 items-center justify-center",
            ICON_SIZE_CLASSES[props.size ?? "md"],
            ICON_SHAPE_CLASSES[props.iconShape ?? "rounded"],
          )}
          style={{
            backgroundColor: accentStyle.backgroundColor,
            color: accentStyle.color,
          }}
        >
          <Icon className="h-full w-full" aria-hidden="true" />
        </span>
      )}
    </div>
  );
}

/** Formatted value (prefix before, suffix after) + optional trend chip. */
function CardValueRow({
  state,
  props,
}: {
  state: Extract<MetricCardState, { type: "ready" }>;
  props: MetricCardProps;
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
      <span
        className={cn(
          "truncate font-display font-bold tracking-tight",
          VALUE_SIZE_CLASSES[props.size ?? "md"],
        )}
      >
        {props.prefix}
        {formatMetricValue(state.value, props.decimals ?? 0)}
        {props.suffix}
      </span>
      {state.trend && (
        <TrendIndicator
          direction={state.trend.direction}
          tone={state.trend.tone}
          value={state.trend.value}
          label={state.trend.label}
        />
      )}
    </div>
  );
}

/** Ready body: header, value row, comparison, description, progress, extras. */
function ReadyStateContent({
  state,
  props,
}: {
  state: Extract<MetricCardState, { type: "ready" }>;
  props: MetricCardProps;
}) {
  const isInline = props.visualizationLayout === "inline" && props.visualization;

  return (
    <>
      <CardHeader props={props} />
      {isInline ? (
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {!props.hideValue && <CardValueRow state={state} props={props} />}
            {props.description && (
              <p className="text-xs leading-relaxed text-base-content/70">{props.description}</p>
            )}
          </div>
          <div className="shrink-0">{props.visualization}</div>
        </div>
      ) : (
        <>
          {!props.hideValue && <CardValueRow state={state} props={props} />}
          {props.description && (
            <p className="text-xs leading-relaxed text-base-content/70">{props.description}</p>
          )}
          {props.visualization && <div className="mt-2 w-full">{props.visualization}</div>}
        </>
      )}
      {!isInline && props.hideValue && state.trend && (
        <TrendIndicator
          direction={state.trend.direction}
          tone={state.trend.tone}
          value={state.trend.value}
          label={state.trend.label}
        />
      )}
      {!isInline && state.comparison && (
        <span className="block truncate text-xs text-base-content/70">{state.comparison}</span>
      )}
      {!isInline && state.progress && (
        <CardProgress progress={state.progress} accent={props.accent ?? "neutral"} />
      )}
      {!isInline && props.actions && props.actions.length > 0 && (
        <CardActions actions={props.actions} />
      )}
    </>
  );
}

/** Top-left accent stripe (mockup parity): 80×3px token-colored contrast bar. */
function AccentStripe({ accent }: { accent: MetricAccent }) {
  return (
    <span
      aria-hidden="true"
      className="absolute left-0 top-0 h-[3px] w-20 rounded-br-md opacity-90"
      style={{ backgroundColor: resolveAccentStyle(accent).color }}
    />
  );
}

/** Stale badge — top-right, overdue token pair, explicit aria-label. */
function StaleBadge() {
  return (
    <span
      className="absolute right-2 top-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap"
      style={{
        color: "var(--status-overdue)",
        backgroundColor: "var(--status-overdue-bg)",
      }}
      aria-label="Données potentiellement obsolètes"
    >
      Données anciennes
    </span>
  );
}

/** Body switch per state (loading never reaches here — short-circuited earlier). */
function StateContent({ state, props }: { state: MetricCardState; props: MetricCardProps }) {
  switch (state.type) {
    case "error":
      return <ErrorStateContent message={state.message} onRetry={state.onRetry} />;
    case "empty":
      return <EmptyStateContent message={state.message} icon={state.icon} action={state.action} />;
    case "ready":
      return <ReadyStateContent state={state} props={props} />;
    case "loading":
      return null;
  }
}

/** Enter/Space activation for role="button" cards. */
function handleCardKeyDown(event: KeyboardEvent<HTMLDivElement>, onClick: () => void) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onClick();
  }
}

/**
 * Shell classes: entrance animation, motion-token transition, focus ring,
 * size/variant chrome. Error bg is applied via inline style (token wins
 * over variant background without !important).
 */
function buildRootClassName(props: MetricCardProps, isClickable: boolean): string {
  return cn(
    "metric-card-root",
    "relative flex animate-fade-in-up flex-col overflow-hidden rounded-xl outline-none",
    "transition-[box-shadow,transform,border-color,background-color]",
    "duration-[var(--duration-base)] ease-[var(--ease-out)]",
    "focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface)]",
    SIZE_CLASSES[props.size ?? "md"],
    VARIANT_CLASSES[props.variant ?? "default"],
    isClickable && "cursor-pointer",
    props.state.type === "empty" && "bg-base-200/50",
    props.className,
  );
}

/**
 * Unified KPI card (plan-metric-cards-unification §4).
 * Display-only brick: parent owns data + state; colors come exclusively from
 * semantic tokens. Keyboard/AT contract: clickable cards expose role="button"
 * (or TanStack <Link> for internal href / native <a> for external) with
 * Enter/Space activation and focus-visible ring. Internal hrefs navigate SPA
 * (no full reload) — rule 6 of check-governance.
 */
export function MetricCard(props: MetricCardProps) {
  const { state, href, onClick, stale = false } = props;

  // Loading short-circuit: skeleton replaces the whole card.
  if (state.type === "loading") {
    return (
      <MetricCardSkeleton
        className={props.className}
        hasVisualization={Boolean(props.visualization)}
      />
    );
  }

  const isClickable = Boolean(href || onClick);
  const rootClassName = buildRootClassName(props, isClickable);
  const accentColor = resolveAccentStyle(props.accent ?? "neutral").color;
  const rootStyle = {
    "--accent-color": accentColor,
    ...(state.type === "error" ? { backgroundColor: "var(--status-rejected-bg)" } : {}),
  } as React.CSSProperties;
  const content = (
    <>
      <AccentStripe accent={props.accent ?? "neutral"} />
      {stale && <StaleBadge />}
      <StateContent state={state} props={props} />
    </>
  );

  if (href) {
    // Internal app paths ("/…", never "//host") render as TanStack <Link> —
    // SPA navigation (rule 6 of check-governance); external / download URLs
    // keep the native anchor.
    const isInternalPath = href.startsWith("/") && !href.startsWith("//");

    return isInternalPath ? (
      <Link to={href} className={rootClassName} style={rootStyle}>
        {content}
      </Link>
    ) : (
      <a href={href} className={rootClassName} style={rootStyle}>
        {content}
      </a>
    );
  }

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={rootClassName}
        style={rootStyle}
        onClick={onClick}
        onKeyDown={(event) => handleCardKeyDown(event, onClick)}
      >
        {content}
      </div>
    );
  }

  return (
    <div className={rootClassName} style={rootStyle}>
      {content}
    </div>
  );
}
